import * as React from 'react';
import { connect } from 'react-redux';

import { t } from 'utils/language';
import { isOverdue } from 'utils/date';

import DeliverableMedia from './media/deliverable-media/DeliverableMedia';

import {
  downloadDeliverableAttachments,
  removeDeliverableAttachment,
  resetUploadState
} from 'modules/deliverables/reducers/deliverableMediaAttachments';
import { removeDeliverablePost } from 'modules/subcampaigns/reducers/offerDeliverables';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import type {
  Deliverable,
  DeliverableAttachment,
  DeliverableUploadedEventAction,
  Subcampaign,
  SubcampaignInfluencerProfile
} from 'types';

type IdOrTmpId = number | string;

type OwnProps = {
  deliverablesContainerRef: React.RefObject<any>,
  campaignId: number,
  deliverable: Deliverable,
  subcampaign: Subcampaign,
  canModify: boolean,
  isForBrand: boolean,
  onApprovalMode: () => void,
  onTrackDeliverableUploadEvent: (eventAction: DeliverableUploadedEventAction) => void
};

type DispatchProps = {
  dispatchDownloadAttachments: (
    e: number,
    d: SubcampaignInfluencerProfile | undefined | null,
    c: Deliverable,
    b?: string | null,
    a?: boolean | null
  ) => void,
  dispatchShowConfirmationModal: (
    params: {
      message: string,
      confirmButtonLabel: string,
      onConfirm: () => void,
      onCancel: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchRemoveDeliverablePost: (b: number, a: number) => Promise<void>,
  dispatchRemoveDeliverableAttachment: (c: number, b: number, a: IdOrTmpId) => Promise<void>,
  dispatchResetAttachmentsUploadState: (a: number) => Promise<void>
};

type Props = OwnProps & DispatchProps;

class DeliverableTabMedia extends React.PureComponent<Props> {
  componentWillUnmount() {
    const { deliverable, dispatchResetAttachmentsUploadState } = this.props;

    dispatchResetAttachmentsUploadState(deliverable.id);
  }

  handleDeletePost = (): void => {
    const {
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal,
      dispatchRemoveDeliverablePost,
      subcampaign,
      deliverable
    } = this.props;

    dispatchShowConfirmationModal({
      message: t(
        'deliverable_media.post_delete_confirmation.message',
        { postType: t(`deliverable_media.posts.${deliverable.deliverableType}`) }
      ),
      confirmButtonLabel: t('deliverable_media.post_delete_confirmation.button_confirm'),
      onConfirm: () =>
        dispatchRemoveDeliverablePost(subcampaign.offer.id, deliverable.id)
          .then(() => dispatchHideConfirmationModal()),
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  handleDeleteAttachment = (attachment: DeliverableAttachment): void => {
    const {
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal,
      dispatchRemoveDeliverableAttachment,
      subcampaign,
      deliverable
    } = this.props;

    dispatchShowConfirmationModal({
      message: t('deliverable_media.attachment_delete_confirmation.message'),
      confirmButtonLabel: t('deliverable_media.attachment_delete_confirmation.button_confirm'),
      onConfirm: () =>
        dispatchRemoveDeliverableAttachment(subcampaign.offer.id, deliverable.id, attachment.id)
          .then(() => dispatchHideConfirmationModal()),
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  handleDownloadAttachments = (attachmentType?: string | null, approvedOnly?: boolean | null): void => {
    const { campaignId, subcampaign, deliverable, dispatchDownloadAttachments } = this.props;
    dispatchDownloadAttachments(campaignId, subcampaign.creatorProfile, deliverable, attachmentType, approvedOnly);
  };

  render(): React.ReactNode {
    const {
      deliverablesContainerRef,
      deliverable,
      subcampaign,
      canModify,
      isForBrand,
      onApprovalMode,
      onTrackDeliverableUploadEvent
    } = this.props;

    const showValidation = isOverdue(deliverable.endDate);

    return (
      <section className="deliverable-tab-media">
        <DeliverableMedia
          deliverablesContainerRef={deliverablesContainerRef}
          canModify={canModify}
          isForBrand={isForBrand}
          showValidation={showValidation}
          subcampaign={subcampaign}
          deliverable={deliverable}
          onApprovalMode={onApprovalMode}
          onDeletePost={this.handleDeletePost}
          onDeleteAttachment={this.handleDeleteAttachment}
          onDownloadAttachments={this.handleDownloadAttachments}
          onTrackDeliverableUploadEvent={onTrackDeliverableUploadEvent}
        />
      </section>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDownloadAttachments: (
    campaignId: number,
    creatorProfile: SubcampaignInfluencerProfile | undefined | null,
    deliverable: Deliverable,
    attachmentType?: string | null,
    approvedOnly?: boolean | null
  ) => {
    dispatch(downloadDeliverableAttachments(campaignId, creatorProfile, deliverable, attachmentType, approvedOnly));
  },

  dispatchShowConfirmationModal: (params) => {
    dispatch(showConfirmModal(params));
  },

  dispatchHideConfirmationModal: () => {
    dispatch(hideConfirmModal());
  },

  dispatchRemoveDeliverablePost: (offerId, deliverableId) => dispatch(removeDeliverablePost(offerId, deliverableId)),
  dispatchRemoveDeliverableAttachment: (offerId, deliverableId, attachmentId) => dispatch(removeDeliverableAttachment(offerId, deliverableId, attachmentId)),
  dispatchResetAttachmentsUploadState: (deliverableId) => dispatch(resetUploadState(deliverableId))
});

export default connect(null, mapDispatchToProps)(DeliverableTabMedia);
